import { useEffect, useReducer } from "react";
import InsuranceService from "../../../services/InsuranceService/InsuranceService.ts";
import { LifecycleService } from "../../../services/Integrations/LifecycleService.ts";
import { isRecord } from "../../../services/Integrations/lifecycleValidation.ts";
import type {
  LocalPaymentMethod,
  ProviderId,
} from "../../../types/lifecycle.ts";
import { useLookup } from "./useLookup.ts";

interface Selection {
  cid: string;
  companyKey: string;
  divisionId: string;
  agentId: string;
  commission: string;
  commissionKey: string;
  additionalData: string;
  paymentMethod: LocalPaymentMethod;
}
type Action =
  | { type: "company"; value: string; key: string }
  | { type: "office" | "agent" | "additional"; value: string }
  | { type: "commission"; value: string; key: string }
  | { type: "payment"; value: LocalPaymentMethod };
const reducer = (state: Selection, action: Action): Selection => {
  switch (action.type) {
    case "company":
      return {
        ...state,
        cid: action.value,
        companyKey: action.key,
        commission: "",
        commissionKey: "",
      };
    case "office":
      return {
        ...state,
        divisionId: action.value,
        commission: "",
        commissionKey: "",
      };
    case "agent":
      return {
        ...state,
        agentId: action.value,
        commission: "",
        commissionKey: "",
      };
    case "commission":
      return { ...state, commission: action.value, commissionKey: action.key };
    case "additional":
      return { ...state, additionalData: action.value };
    case "payment":
      return { ...state, paymentMethod: action.value };
  }
};
const options = (value: unknown): Record<string, string> => {
  if (
    !isRecord(value) ||
    !Object.values(value).every((label) => typeof label === "string")
  )
    throw new Error("Invalid booking directory");
  return value as Record<string, string>;
};
// BASE_ZK values from Wing's conf/ins_comp.xml. The returned directory keys
// are the deployment's local INS_COMP.CID values used for booking.
export const providerBaseIds: Record<ProviderId, string> = {
  ozk: "10",
  dallbogg: "14",
  dzi: "6",
  generali: "5",
  bulins: "2",
  levins: "9",
  armeec: "1",
  euroins: "8",
};
export function useBooking(
  jwt: string,
  provider: ProviderId | "",
  headOff: string
) {
  const [selection, dispatch] = useReducer(reducer, {
    cid: "",
    companyKey: "",
    divisionId: "",
    agentId: "",
    commission: "",
    commissionKey: "",
    additionalData: "",
    paymentMethod: "cash",
  });
  const companyKey = jwt && provider ? `${jwt}|${provider}` : "";
  const companyDirectory = useLookup(companyKey, async () => {
    const rows = options(
      await InsuranceService.getAllInsuranceCompanies(
        jwt,
        false,
        providerBaseIds[provider as ProviderId]
      )
    );
    if (!Object.keys(rows).length)
      throw new Error("Няма достъпни агенции за избрания застраховател.");
    return rows;
  });
  const companies = companyDirectory.value ?? {};
  const cid =
    selection.companyKey === companyKey && companies[selection.cid]
      ? selection.cid
      : "";
  const directories = useLookup(jwt, async () => {
    const [officeRows, agentRows] = await Promise.all([
      InsuranceService.getAllOffices(jwt, false),
      InsuranceService.getAllAgents(jwt),
    ]);
    return { offices: options(officeRows), agents: options(agentRows) };
  });
  const offices = directories.value?.offices ?? {};
  const agents = directories.value?.agents ?? {};
  const { divisionId } = selection;
  useEffect(() => {
    if (divisionId) return;
    const ids = Object.keys(offices);
    const defaultOffice = offices[headOff]
      ? String(headOff)
      : ids.length === 1
      ? ids[0]
      : "";
    if (defaultOffice) dispatch({ type: "office", value: defaultOffice });
  }, [directories.value, divisionId, headOff]);
  const agentId = agents[selection.agentId] ? selection.agentId : "";
  const commissionKey =
    jwt && provider && cid && divisionId && agentId
      ? `${jwt}|${provider}|${cid}|${divisionId}|${agentId}`
      : "";
  const defaultCommission = useLookup(commissionKey, () =>
    LifecycleService.getLocalBookingCommission(
      jwt,
      provider as ProviderId,
      divisionId,
      agentId,
      cid
    )
  );
  const commission =
    selection.commissionKey === commissionKey && commissionKey
      ? selection.commission
      : defaultCommission.value
      ? String(defaultCommission.value.commissionPercentage)
      : "";
  const commissionNumber = Number(commission.replace(",", "."));
  const valid = Boolean(
    cid &&
      offices[divisionId] &&
      agentId &&
      commission.trim() &&
      Number.isFinite(commissionNumber) &&
      commissionNumber >= 0 &&
      commissionNumber <= 100
  );
  return {
    companies,
    cid,
    companiesLoading: companyDirectory.loading,
    offices,
    agents,
    divisionId,
    agentId,
    commission,
    commissionNumber,
    additionalData: selection.additionalData,
    paymentMethod: selection.paymentMethod,
    valid,
    commissionLoading: defaultCommission.loading,
    error:
      companyDirectory.error || directories.error || defaultCommission.error,
    setCid: (value: string) =>
      dispatch({ type: "company", value, key: companyKey }),
    setDivisionId: (value: string) => dispatch({ type: "office", value }),
    setAgentId: (value: string) => dispatch({ type: "agent", value }),
    setCommission: (value: string) =>
      dispatch({ type: "commission", value, key: commissionKey }),
    setAdditionalData: (value: string) =>
      dispatch({ type: "additional", value }),
    setPaymentMethod: (value: LocalPaymentMethod) =>
      dispatch({ type: "payment", value }),
  };
}
